import {
  logout as bffLogout,
  setAdminBffAccessToken,
  setRefreshCallback,
} from '@avila-roffe/api-clients-admin-bff';
import { toast } from '@avila-roffe/components-web';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { buildThemeParams } from '../app/lib/themeParams';
import { useAuthStore } from '../stores/authStore';
import { useTenantContext } from '../tenant/TenantContext';
import { AuthContext, type AuthUser } from './AuthContext';

export const AUTH_RETURN_PATH_KEY = 'demo:auth-return-path';

const BFF_URL = (import.meta.env.VITE_ADMIN_BFF_URL as string | undefined) || '';

const IDLE_WARN_MS = 28 * 60 * 1000;
const IDLE_LOGOUT_MS = 30 * 60 * 1000;
const IDLE_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'] as const;

interface TokenPair {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
}

interface JwtPayload {
  sub: string;
  email?: string;
  name?: string;
  tenant_slug: string;
  roles: string[];
}

function parseJwtPayload(token: string): JwtPayload | null {
  try {
    const base64 = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? '';
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return null;
  }
}

class AuthTokenError extends Error {
  constructor(
    message: string,
    public readonly isInvalidToken: boolean,
    public readonly isRateLimited = false,
  ) {
    super(message);
  }
}

// Refresh via httpOnly cookie (standard) with stored refresh token in body as fallback
// for PWA clients on iOS where the cookie jar is isolated from Safari.
async function refreshBffToken(): Promise<TokenPair> {
  const { refreshToken: storedRefreshToken } = useAuthStore.getState();
  const res = await fetch(`${BFF_URL}/api/v1/auth/token/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: storedRefreshToken ? JSON.stringify({ refresh_token: storedRefreshToken }) : '{}',
  });
  if (!res.ok) {
    const isInvalidToken = res.status === 401 || res.status === 403;
    const isRateLimited = res.status === 429;
    throw new AuthTokenError(`BFF refresh failed: ${res.status}`, isInvalidToken, isRateLimited);
  }
  const envelope = (await res.json()) as { data: TokenPair };
  return envelope.data;
}

function userFromToken(accessToken: string): AuthUser | null {
  const payload = parseJwtPayload(accessToken);
  if (!payload) return null;
  return {
    id: payload.sub,
    email: payload.email ?? payload.sub,
    displayName: payload.name,
    tenantSlug: payload.tenant_slug,
    roles: payload.roles,
  };
}

/** Called by AuthCallbackView after receiving the token pair from the login portal. */
export function storeTokenPair(pair: TokenPair): void {
  useAuthStore.getState().setTokens(pair.accessToken, pair.expiresIn, pair.refreshToken);
}

function sanitizeReturnPath(raw: string): string {
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes(':')) return '/';
  return raw;
}

interface AuthProviderProps {
  children: ReactNode;
}

const ENV_LOGIN_PORTAL_URL = (import.meta.env.VITE_LOGIN_PORTAL_URL as string | undefined) ?? '';
const APP_ID = (import.meta.env.VITE_APP_ID as string | undefined) || 'mytaible-waiter';

export function AuthProvider({ children }: AuthProviderProps) {
  const { config, slug: tenantSlug } = useTenantContext();
  const loginPortalUrl =
    ENV_LOGIN_PORTAL_URL || config?.loginPortalURL || config?.loginPortalUrl || '';
  const slug = tenantSlug ?? '';

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError] = useState<string | null>(null);

  const initRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doRefreshRef = useRef<() => Promise<string | null>>(async () => null);

  const idleWarnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleLogoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnToastShownRef = useRef(false);

  const clearSession = useCallback(() => {
    setAdminBffAccessToken(null);
    useAuthStore.getState().clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (idleWarnTimerRef.current) clearTimeout(idleWarnTimerRef.current);
    if (idleLogoutTimerRef.current) clearTimeout(idleLogoutTimerRef.current);
  }, []);

  const redirectToLogin = useCallback(
    (returnPath?: string) => {
      if (!loginPortalUrl) return;
      const savePath = returnPath ?? sanitizeReturnPath(window.location.pathname);
      if (savePath !== '/auth/callback') {
        sessionStorage.setItem(AUTH_RETURN_PATH_KEY, savePath);
      }
      const callbackUrl = `${window.location.origin}/auth/callback`;
      window.location.replace(
        `${loginPortalUrl}/login?tenantSlug=${encodeURIComponent(slug)}&app=${APP_ID}&redirectUri=${encodeURIComponent(callbackUrl)}${buildThemeParams()}`,
      );
    },
    [loginPortalUrl, slug],
  );

  const logout = useCallback(async () => {
    try {
      await bffLogout();
    } catch {
      // BFF unreachable — proceed anyway
    }
    clearSession();
    redirectToLogin('/');
  }, [clearSession, redirectToLogin]);

  const applyTokenPair = useCallback((pair: TokenPair) => {
    storeTokenPair(pair);
    setAdminBffAccessToken(pair.accessToken);
    setUser(userFromToken(pair.accessToken));
    setIsAuthenticated(true);

    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max((pair.expiresIn - 60) * 1000, 10_000);
    refreshTimerRef.current = setTimeout(() => void doRefreshRef.current(), delay);
  }, []);

  const resetIdleTimers = useCallback(() => {
    if (idleWarnTimerRef.current) clearTimeout(idleWarnTimerRef.current);
    if (idleLogoutTimerRef.current) clearTimeout(idleLogoutTimerRef.current);
    warnToastShownRef.current = false;

    idleWarnTimerRef.current = setTimeout(() => {
      if (!warnToastShownRef.current) {
        warnToastShownRef.current = true;
        toast({
          title: 'Session expiring',
          description: 'You will be signed out in 2 minutes due to inactivity.',
        });
      }
    }, IDLE_WARN_MS);

    idleLogoutTimerRef.current = setTimeout(() => {
      void logout();
    }, IDLE_LOGOUT_MS);
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    resetIdleTimers();
    for (const ev of IDLE_EVENTS) {
      window.addEventListener(ev, resetIdleTimers, { passive: true });
    }
    return () => {
      for (const ev of IDLE_EVENTS) {
        window.removeEventListener(ev, resetIdleTimers);
      }
      if (idleWarnTimerRef.current) clearTimeout(idleWarnTimerRef.current);
      if (idleLogoutTimerRef.current) clearTimeout(idleLogoutTimerRef.current);
    };
  }, [isAuthenticated, resetIdleTimers]);

  // ── Token refresh ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const doRefresh = async (): Promise<string | null> => {
      try {
        const pair = await refreshBffToken();
        applyTokenPair(pair);
        return pair.accessToken;
      } catch {
        clearSession();
        return null;
      }
    };
    doRefreshRef.current = doRefresh;
    setRefreshCallback(doRefresh);
    return () => setRefreshCallback(null);
  }, [applyTokenPair, clearSession]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      setIsLoading(true);

      // Use stored access token if it's still valid.
      const { accessToken: storedAccess, tokenExpiry: storedExpiry } = useAuthStore.getState();
      if (storedAccess && storedExpiry !== null && storedExpiry > Date.now() + 60_000) {
        setAdminBffAccessToken(storedAccess);
        setUser(userFromToken(storedAccess));
        setIsAuthenticated(true);
        const delay = Math.max(storedExpiry - Date.now() - 60_000, 10_000);
        refreshTimerRef.current = setTimeout(() => void doRefreshRef.current(), delay);
        setIsLoading(false);
        return;
      }

      // Access token expired or absent — attempt silent refresh via httpOnly cookie.
      if (window.location.pathname === '/auth/callback') {
        setIsLoading(false);
        return;
      }

      let pair: TokenPair | null = null;
      const retryDelays = [2000, 4000, 8000];
      let rateLimited = false;
      for (const delay of [0, ...retryDelays]) {
        if (delay > 0) await new Promise<void>((resolve) => setTimeout(resolve, delay));
        try {
          pair = await refreshBffToken();
          break;
        } catch (err) {
          if (err instanceof AuthTokenError) {
            if (err.isInvalidToken) break;
            if (err.isRateLimited) {
              rateLimited = true;
              break;
            }
          }
        }
      }

      if (pair) {
        applyTokenPair(pair);
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      // Rate limited: session may still be valid once the window clears — don't redirect.
      if (!rateLimited) redirectToLogin();
    };

    void init();
  }, [applyTokenPair, clearSession, redirectToLogin]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, isLoading, authError, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
